import "./styles.css";

import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import createGlobe from "cobe";
import usePartySocket from "partysocket/react";

// The type of messages we'll be receiving from the server
import type { OutgoingMessage } from "../shared";

type Page = "home" | "products" | "projects" | "about" | "contact";

function Globe() {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [counter, setCounter] = useState(0);
	const positions = useRef<
		Map<
			string,
			{
				location: [number, number];
				size: number;
			}
		>
	>(new Map());

	const socket = usePartySocket({
		room: "default",
		party: "globe",
		onMessage(evt) {
			const message = JSON.parse(evt.data as string) as OutgoingMessage;
			if (message.type === "add-marker") {
				positions.current.set(message.position.id, {
					location: [message.position.lat, message.position.lng],
					size: message.position.id === socket.id ? 0.1 : 0.05,
				});
				setCounter((c) => c + 1);
			} else {
				positions.current.delete(message.id);
				setCounter((c) => c - 1);
			}
		},
	});

	useEffect(() => {
		if (!canvasRef.current) return;
		let phi = 0;

		const globe = createGlobe(canvasRef.current, {
			devicePixelRatio: 2,
			width: 400 * 2,
			height: 400 * 2,
			phi: 0,
			theta: 0,
			dark: 1,
			diffuse: 0.8,
			mapSamples: 16000,
			mapBrightness: 6,
			baseColor: [0.3, 0.3, 0.3],
			markerColor: [0.8, 0.1, 0.1],
			glowColor: [0.2, 0.2, 0.2],
			markers: [],
			opacity: 0.7,
			onRender: (state) => {
				state.markers = [...positions.current.values()];
				state.phi = phi;
				phi += 0.01;
			},
		});

		return () => {
			globe.destroy();
		};
	}, []);

	return (
		<div className="globe-container">
			<canvas
				ref={canvasRef}
				style={{ width: 400, height: 400, maxWidth: "100%", aspectRatio: 1 }}
			/>
			{counter !== 0 && (
				<p className="counter">
					<b>{counter}</b> {counter === 1 ? "person" : "people"} connected
				</p>
			)}
		</div>
	);
}

function Navigation({ currentPage, setCurrentPage }: { currentPage: Page; setCurrentPage: (page: Page) => void }) {
	const pages: { key: Page; label: string }[] = [
		{ key: "home", label: "Home" },
		{ key: "products", label: "Products" },
		{ key: "projects", label: "Projects" },
		{ key: "about", label: "About" },
		{ key: "contact", label: "Contact" },
	];

	return (
		<nav className="navbar">
			<div className="nav-logo">⚡ MATECHPOWER</div>
			<div className="nav-links">
				{pages.map((page) => (
					<button
						key={page.key}
						className={currentPage === page.key ? "active" : ""}
						onClick={() => setCurrentPage(page.key)}
					>
						{page.label}
					</button>
				))}
			</div>
		</nav>
	);
}

function HomePage() {
	return (
		<div className="page home-page">
			<h1 className="hero-title">Reduce Emissions & Fuel Consumption</h1>
			<p className="hero-subtitle">
				MATECHPOWER device transforms molecular structure of fuel — no external energy, no maintenance, unlimited lifetime guarantee.
			</p>
			<Globe />
			<div className="stats-grid">
				<div className="stat-card">
					<h3>Marine Industry</h3>
					<p>150+ commercial vessels retrofitted</p>
				</div>
				<div className="stat-card">
					<h3>Logistics</h3>
					<p>2000+ trucks with 15% fuel savings</p>
				</div>
				<div className="stat-card">
					<h3>Power Generation</h3>
					<p>8-12% efficiency increase</p>
				</div>
			</div>
		</div>
	);
}

function ProductsPage() {
	const products = [
		{
			name: "MATECHPOWER M-Series",
			tag: "Best Seller",
			description: "For diesel engines up to 500HP. Perfect for trucks, buses, construction equipment.",
			features: ["12-18% fuel savings", "20-30% emission reduction", "Installation: 30 minutes"],
		},
		{
			name: "MATECHPOWER Marine PRO",
			tag: "Industrial",
			description: "For marine diesel engines, generators, and auxiliary engines on vessels.",
			features: ["10-15% marine diesel savings", "5000+ operating hours", "Corrosion-resistant housing"],
		},
		{
			name: "MATECHPOWER G-Series",
			tag: "Universal",
			description: "For gasoline, LPG, and natural gas engines. Cars, vans, small generators.",
			features: ["8-12% fuel economy", "Lower octane fuel compatible", "Unlimited lifetime warranty"],
		},
		{
			name: "MATECHPOWER Turbine X",
			tag: "Heavy Duty",
			description: "For gas turbines, industrial burners, and power generation systems.",
			features: ["6-10% efficiency increase", "Lower exhaust temperature", "24/7 continuous operation"],
		},
	];

	return (
		<div className="page products-page">
			<h1>MATECHPOWER Product Line</h1>
			<p className="page-subtitle">Engineered for every combustion application</p>
			<div className="products-grid">
				{products.map((product, index) => (
					<div key={index} className="product-card">
						<span className="product-tag">{product.tag}</span>
						<h3>{product.name}</h3>
						<p>{product.description}</p>
						<ul>
							{product.features.map((feature, i) => (
								<li key={i}>{feature}</li>
							))}
						</ul>
					</div>
				))}
			</div>
			<div className="how-it-works">
				<h2>How It Works</h2>
				<div className="steps">
					<div className="step">
						<div className="step-number">1</div>
						<h4>Fuel passes through</h4>
						<p>Fuel flows through the MATECHPOWER device before reaching injectors.</p>
					</div>
					<div className="step">
						<div className="step-number">2</div>
						<h4>Resonance modification</h4>
						<p>Magnetic field reorganizes hydrocarbon molecules at the atomic level.</p>
					</div>
					<div className="step">
						<div className="step-number">3</div>
						<h4>Perfect combustion</h4>
						<p>Optimized fuel burns completely, maximizing power and minimizing emissions.</p>
					</div>
				</div>
			</div>
		</div>
	);
}

function ProjectsPage() {
	const projects = [
		{
			name: "Maersk Fleet Retrofit",
			stats: ["14% fuel saving", "-22% CO₂"],
			description: "Container vessel retrofitted with MATECHPOWER Marine PRO. After 12 months: $240,000 annual fuel savings per vessel.",
		},
		{
			name: "DHL Fleet UK",
			stats: ["16% fuel saving", "5000+ hrs"],
			description: "350 delivery vans equipped with G-Series. Extended oil change intervals from 20k to 35k miles.",
		},
		{
			name: "German Power Plant",
			stats: ["9% efficiency gain", "-18% NOx"],
			description: "Gas turbine installation (50MW). Payback period: 8 months. Emission compliance achieved.",
		},
		{
			name: "Indian Railways",
			stats: ["11% fuel saving", "100+ locos"],
			description: "Pilot program on 50 diesel locomotives expanding to 500+ units.",
		},
	];

	return (
		<div className="page projects-page">
			<h1>Real Projects, Real Results</h1>
			<p className="page-subtitle">Global installations across maritime, logistics, and energy sectors</p>
			<div className="projects-grid">
				{projects.map((project, index) => (
					<div key={index} className="project-card">
						<div className="project-icon">
							{index === 0 && "🚢"}
							{index === 1 && "🚚"}
							{index === 2 && "🏭"}
							{index === 3 && "🚂"}
						</div>
						<div className="project-stats">
							{project.stats.map((stat, i) => (
								<span key={i}>{stat}</span>
							))}
						</div>
						<h3>{project.name}</h3>
						<p>{project.description}</p>
					</div>
				))}
			</div>
		</div>
	);
}

function AboutPage() {
	return (
		<div className="page about-page">
			<h1>About MATECHPOWER</h1>
			<div className="about-grid">
				<div className="about-card">
					<h2>Our Story</h2>
					<p>Founded in 2014, MATECHPOWER UK Ltd has been at the forefront of magnetic resonance technology for combustion optimization. Our mission: reduce global emissions by making every engine burn fuel more efficiently.</p>
					<p>Incorporated in England and Wales, we serve clients across 6 continents.</p>
				</div>
				<div className="about-card">
					<h2>R&D & Innovation</h2>
					<p>Our laboratory in Plymouth continuously develops new resonance patterns for emerging fuels. Patents pending in EU, USA, and Asia.</p>
				</div>
			</div>
			<div className="certificates">
				<h2>Certificates & Compliance</h2>
				<div className="cert-grid">
					<div className="cert-card">ISO 9001:2024</div>
					<div className="cert-card">CE Certified</div>
					<div className="cert-card">RoHS Compliant</div>
				</div>
			</div>
		</div>
	);
}

function ContactPage() {
	return (
		<div className="page contact-page">
			<h1>Contact Us</h1>
			<div className="contact-grid">
				<div className="contact-card">
					<h2>Office Address</h2>
					<p>MATECHPOWER UK Ltd<br />1 Eagle Road, Langage Industrial Park<br />Plympton, Plymouth<br />PL7 5JY<br />United Kingdom</p>
					<p>
						<a href="mailto:info@matechpoweruk.online">info@matechpoweruk.online</a>
					</p>
					<p>+44 (0)20 7991 8888</p>
				</div>
				<div className="contact-card">
					<h2>Business Hours</h2>
					<p>Monday - Friday: 9:00 - 17:00 (GMT)</p>
					<p>Weekend: Closed</p>
					<p>For technical support, please email us.</p>
				</div>
			</div>
			<div className="contact-form">
				<h2>Send a Message</h2>
				<form>
					<input type="text" placeholder="Your Name" />
					<input type="email" placeholder="Email Address" />
					<input type="text" placeholder="Company / Organization" />
					<textarea rows="5" placeholder="Tell us about your project or inquiry..."></textarea>
					<button type="submit">Send Message</button>
				</form>
			</div>
		</div>
	);
}

function App() {
	const [currentPage, setCurrentPage] = useState<Page>("home");

	const renderPage = () => {
		switch (currentPage) {
			case "home":
				return <HomePage />;
			case "products":
				return <ProductsPage />;
			case "projects":
				return <ProjectsPage />;
			case "about":
				return <AboutPage />;
			case "contact":
				return <ContactPage />;
			default:
				return <HomePage />;
		}
	};

	return (
		<div className="App">
			<Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} />
			{renderPage()}
			<footer>
				<p>© 2026 MATECHPOWER UK Ltd — Magnetic Efficiency Solutions</p>
				<p>Company Registration № UK-08883501 | Unlimited lifetime guarantee on all products</p>
			</footer>
		</div>
	);
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("root")!).render(<App />);
